import type { Recipient } from '../../types';
import type { ReactNode } from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useToast } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import {
  CourseAssigner,
  LearnersManager,
} from '~/elc/features/admin-guides/components/AddContactsForLearnerQuestions/constants';
import { useTrainingContacts } from '~/elc/features/admin-guides/hooks';
import { sendLearnerQuestion } from '../../api/sendLearnerQuestion';
import { AskATeammateContext } from './AskATeammateContext';
import { mapContactToRecipient } from './utils';

type Props = {
  children: ReactNode;
  isPreview?: boolean;
  recipients?: Recipient[];
  guideId: string;
  courseTaskAssignmentId?: string | null;
  isDisabled: boolean;
};

const sentMessage = I18n.t('js.learner.guide.task.ask_a_teammate.sent_message');
const errorMessage = I18n.t('js.learner.guide.task.ask_a_teammate.error_message');

export const AskATeammateProvider = ({
  courseTaskAssignmentId,
  guideId,
  isPreview = false,
  isDisabled,
  recipients = [],
  children,
}: Props): JSX.Element => {
  const [activeObjectId, setActiveObjectId] = useState(null);

  const { data: trainingContacts = [] } = useTrainingContacts(guideId, {
    enabled: isPreview && !isDisabled,
  });

  const recipientsList = useMemo(() => {
    if (!isPreview) {
      return recipients;
    }

    return trainingContacts.map((contact) => {
      if (contact.useLearnerManagers) {
        return mapContactToRecipient(LearnersManager);
      }

      if (contact.useLearnerAssigner) {
        return mapContactToRecipient(CourseAssigner);
      }

      return mapContactToRecipient(contact.contact);
    });
  }, [isPreview, recipients, trainingContacts]);

  const { open: openToast } = useToast();

  const showToastMessage = useCallback(
    (text) => {
      openToast({
        children: text,
      });
    },
    [openToast],
  );

  const sendQuestion = useCallback(
    (documentObjectId: string, message: string) => {
      if (isPreview) {
        showToastMessage('Messages cannot be sent in Preview Mode');
        return;
      }

      sendLearnerQuestion({ guideId, documentObjectId, courseTaskAssignmentId, message })
        .then(() => showToastMessage(sentMessage))
        .catch(() => showToastMessage(errorMessage));
    },
    [courseTaskAssignmentId, guideId, isPreview, showToastMessage],
  );

  const value = useMemo(
    () => ({ activeObjectId, setActiveObjectId, recipients: recipientsList, sendQuestion }),
    [activeObjectId, recipientsList, sendQuestion],
  );

  return <AskATeammateContext.Provider value={value}>{children}</AskATeammateContext.Provider>;
};
